/**
 * Klient HTTP ActaModus.
 *
 * Zasady (CONTRACTS.md §3):
 *  - baza `/api` (w dev proxy Vite → http://localhost:8000),
 *  - access token TYLKO w pamięci (nigdy localStorage), refresh w httpOnly cookie,
 *  - na 401 jednokrotna próba `POST /api/auth/refresh` i powtórzenie żądania,
 *  - `fetch`, bez axios.
 */

import type { LoginResponse, User } from './types'

export const API_BASE = '/api'

/* ------------------------------------------------------------ token */

let accessToken: string | null = null

export const tokenStore = {
  get(): string | null {
    return accessToken
  },
  set(token: string | null): void {
    accessToken = token
  },
  clear(): void {
    accessToken = null
  },
}

/** Emitowane, gdy sesja wygasła bezpowrotnie (refresh nie powiódł się). */
export const AUTH_EXPIRED_EVENT = 'actamodus:auth-expired'

function emitAuthExpired(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT))
  }
}

/* ------------------------------------------------------------ błędy */

export class ApiError extends Error {
  readonly status: number
  readonly detail: unknown
  readonly url: string

  constructor(status: number, message: string, detail: unknown, url: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
    this.url = url
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  /** Komunikat gotowy do pokazania użytkownikowi. */
  get userMessage(): string {
    switch (this.status) {
      case 401:
        return 'Sesja wygasła. Zaloguj się ponownie.'
      case 403:
        return 'Brak uprawnień do tej operacji.'
      case 404:
        return 'Nie znaleziono zasobu.'
      case 0:
        return 'Brak połączenia z serwerem.'
      default:
        return this.message || 'Wystąpił błąd serwera.'
    }
  }
}

/* ------------------------------------------------------------ pomocnicze */

export type QueryValue = string | number | boolean | null | undefined
export type Query = Record<string, QueryValue | QueryValue[]>

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Ciało JSON (serializowane automatycznie). Dla plików użyj `formData`. */
  body?: unknown
  formData?: FormData
  query?: Query
  signal?: AbortSignal
  headers?: Record<string, string>
  /** Pomija auto-refresh (używane wewnętrznie przez /auth/*). */
  skipAuthRefresh?: boolean
}

function buildUrl(path: string, query?: Query): string {
  const base = path.startsWith('/api') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`
  if (!query) return base
  const params = new URLSearchParams()
  for (const [key, raw] of Object.entries(query)) {
    if (raw === undefined || raw === null || raw === '') continue
    if (Array.isArray(raw)) {
      for (const v of raw) {
        if (v === undefined || v === null || v === '') continue
        params.append(key, String(v))
      }
    } else {
      params.append(key, String(raw))
    }
  }
  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null
  const type = res.headers.get('content-type') ?? ''
  if (type.includes('application/json')) {
    try {
      return await res.json()
    } catch {
      return null
    }
  }
  const text = await res.text()
  return text.length > 0 ? text : null
}

/** FastAPI zwraca `{detail: "..."}` albo listę błędów walidacji. */
function messageFromDetail(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.length > 0) return payload
  if (payload && typeof payload === 'object' && 'detail' in payload) {
    const detail = (payload as { detail: unknown }).detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      const first = detail[0] as { msg?: unknown } | undefined
      if (first && typeof first.msg === 'string') return first.msg
    }
  }
  return fallback
}

/* ------------------------------------------------------------ refresh */

let refreshInFlight: Promise<string | null> | null = null

/** Jedna wspólna próba odświeżenia dla wszystkich równoległych żądań. */
async function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        })
        if (!res.ok) return null
        const data = (await parseBody(res)) as { access_token?: string } | null
        const token = data?.access_token ?? null
        tokenStore.set(token)
        return token
      } catch {
        return null
      } finally {
        // pozwól kolejnym 401 spróbować ponownie w przyszłości
        setTimeout(() => {
          refreshInFlight = null
        }, 0)
      }
    })()
  }
  return refreshInFlight
}

/* ------------------------------------------------------------ request */

async function rawRequest(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }
  let body: BodyInit | undefined
  if (options.formData) {
    body = options.formData
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.body)
  }
  if (token) headers.Authorization = `Bearer ${token}`

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
  }
  if (body !== undefined) init.body = body
  if (options.signal) init.signal = options.signal

  return fetch(buildUrl(path, options.query), init)
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, options.query)
  let res: Response
  try {
    res = await rawRequest(path, options, tokenStore.get())
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError(0, 'Brak połączenia z serwerem.', err, url)
  }

  // 401 → jedna próba odświeżenia tokenu i jedno powtórzenie
  if (res.status === 401 && !options.skipAuthRefresh) {
    const token = await refreshAccessToken()
    if (token) {
      try {
        res = await rawRequest(path, options, token)
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') throw err
        throw new ApiError(0, 'Brak połączenia z serwerem.', err, url)
      }
    } else {
      tokenStore.clear()
      emitAuthExpired()
    }
  }

  const payload = await parseBody(res)

  if (!res.ok) {
    throw new ApiError(res.status, messageFromDetail(payload, res.statusText || 'Błąd żądania'), payload, url)
  }
  return payload as T
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData, options?: Omit<RequestOptions, 'method' | 'body' | 'formData'>) =>
    request<T>(path, { ...options, method: 'POST', formData }),
}

/* ------------------------------------------------------------ auth API */

export const authApi = {
  login: (email: string, password: string) =>
    request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
      skipAuthRefresh: true,
    }),
  /** Zwraca nowy access token albo null (brak/wygasłe cookie). */
  refresh: () => refreshAccessToken(),
  logout: async (): Promise<void> => {
    try {
      await request<unknown>('/auth/logout', { method: 'POST', skipAuthRefresh: true })
    } catch {
      // wylogowanie lokalne musi się udać nawet gdy backend nie odpowiada
    } finally {
      tokenStore.clear()
    }
  },
  me: () => request<User>('/auth/me'),
}
